import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Download, FileText, Clock, ShieldAlert, Loader2 } from 'lucide-react';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function DigitalDownloadPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchInfo = async () => {
    try {
      const res = await axios.get(`${API}/digital/download-info/${token}`);
      setInfo(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Link tidak ditemukan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInfo(); }, [token]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/digital/download/${token}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = info?.file_name || 'produk-digital';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      fetchInfo();
    } catch (e) {
      let detail = 'Unduhan gagal';
      try {
        detail = JSON.parse(await e.response.data.text()).detail || detail;
      } catch (_) { /* biarkan pesan default */ }
      setError(detail);
    } finally {
      setDownloading(false);
    }
  };

  const blocked = info && info.state !== 'ok';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 shadow-sm" data-testid="download-card">
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-slate-500 py-8">
            <Loader2 className="w-4 h-4 animate-spin" /> Memuat...
          </div>
        ) : error && !info ? (
          <div className="text-center py-6" data-testid="download-error">
            <ShieldAlert className="w-10 h-10 mx-auto text-red-500 mb-3" />
            <p className="font-semibold text-slate-900 dark:text-zinc-50">{error}</p>
            <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">Hubungi penjual untuk mendapatkan link baru.</p>
          </div>
        ) : (
          <>
            <p className="text-xs uppercase tracking-wide text-indigo-600 dark:text-indigo-400 font-semibold">{info.store_name}</p>
            <h1 className="text-xl font-bold text-slate-900 dark:text-zinc-50 mt-1" data-testid="download-product-name">{info.product_name}</h1>
            <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">Pesanan {info.order_id}</p>

            <div className="mt-4 rounded-xl border border-slate-200 dark:border-zinc-800 p-3 flex items-center gap-3">
              <FileText className="w-5 h-5 text-indigo-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-zinc-50 truncate" data-testid="download-file-name">{info.file_name}</p>
                <p className="text-xs text-slate-500 dark:text-zinc-400">{formatSize(info.size_bytes)}</p>
              </div>
            </div>

            <div className="mt-3 space-y-1 text-xs text-slate-500 dark:text-zinc-400">
              {info.downloads_left !== null && (
                <p data-testid="download-quota">Sisa kuota unduh: <strong className="text-slate-700 dark:text-zinc-200">{info.downloads_left}x</strong> dari {info.download_limit}x</p>
              )}
              {info.expires_at && (
                <p className="flex items-center gap-1"><Clock className="w-3 h-3" /> Berlaku sampai {formatDate(info.expires_at)}</p>
              )}
            </div>

            {error && <p className="mt-3 text-xs text-red-600 dark:text-red-400" data-testid="download-inline-error">{error}</p>}

            {blocked ? (
              <div className="mt-4 rounded-xl bg-red-50 dark:bg-red-950/30 p-3 text-center" data-testid="download-blocked">
                <p className="text-sm font-medium text-red-700 dark:text-red-300">
                  {info.state === 'expired' ? 'Link sudah kedaluwarsa' : 'Batas jumlah unduhan tercapai'}
                </p>
                <p className="text-xs text-red-600/80 dark:text-red-400/80 mt-1">Minta penjual mengirim ulang produknya.</p>
              </div>
            ) : (
              <Button onClick={handleDownload} disabled={downloading}
                className="w-full mt-4 h-11 bg-indigo-600 hover:bg-indigo-700 rounded-xl gap-2" data-testid="download-btn">
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {downloading ? 'Menyiapkan...' : 'Unduh Sekarang'}
              </Button>
            )}

            <p className="text-[11px] text-slate-400 dark:text-zinc-500 mt-4 text-center">
              Link ini pribadi. Jangan dibagikan ke orang lain.
            </p>
            <p className="text-[11px] text-slate-400 dark:text-zinc-500 mt-1.5 text-center" data-testid="refund-note">
              Produk digital tidak dapat dikembalikan atau di-refund setelah file diunduh.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
